/**
 * `extra_info` is a JSON object the backend hands us as a *string*, on both packages and doctors.
 * What is inside is not uniform: flags and labels are strings ("1"/"0"), `doctor_group` and
 * `special_packages` are real arrays, while `facilities` and `extra_fields` are *another* JSON
 * string that has to be parsed a second time. So values stay `unknown` and every read goes through
 * an accessor that checks the shape it needs.
 */
export type IExtraInfo = Record<string, unknown>;

/**
 * Bad JSON from configuration data must not take a page down, so a broken blob reads as empty. Now
 * and then the backend sends the object itself instead of its JSON string; that is taken as is.
 */
export const parseExtraInfo = (raw?: unknown): IExtraInfo => {
  if (!raw) {
    return {};
  }

  if (typeof raw === "object") {
    return Array.isArray(raw) ? {} : (raw as IExtraInfo);
  }

  if (typeof raw !== "string") {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

/** Flags are opt-in: only "1" turns one on, so an absent or "0" value keeps the default behaviour. */
export const isExtraFlagOn = (value: unknown) => value === "1";
